import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NoteComment } from '@/types/note';

interface CommentThreadProps {
  comments: NoteComment[];
  onAddComment: (text: string, replyTo?: string) => void;
  onDeleteComment?: (commentId: string) => void;
}

const CommentThread: React.FC<CommentThreadProps> = ({ comments, onAddComment, onDeleteComment }) => {
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | undefined>();

  const handleSubmit = () => {
    if (newComment.trim()) {
      onAddComment(newComment, replyingTo);
      setNewComment("");
      setReplyingTo(undefined);
    }
  };

  return (
    <div className="p-4 rounded-lg shadow-sm space-y-4 bg-card text-card-foreground">
      <ScrollArea className="h-[200px] mb-4">
        {comments.map((comment) => (
          <div key={comment.id} className={`p-2 ${comment.replyTo ? 'ml-4' : ''}`}>
            <p className="text-sm text-foreground">{comment.text}</p>
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-muted-foreground">{comment.timestamp}</span>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReplyingTo(comment.id)}
                  className="px-3 py-1"
                >
                  Reply
                </Button>
                {onDeleteComment && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteComment(comment.id)}
                    className="px-3 py-1 text-red-600 hover:text-red-700"
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </ScrollArea>
      <div className="flex gap-2 sticky bottom-0 bg-background p-2 border-t border-border">
        <Input
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={replyingTo ? "Write a reply..." : "Add a comment..."}
          className="flex-1"
        />
        <Button 
          onClick={handleSubmit}
          className="whitespace-nowrap"
        >
          {replyingTo ? "Reply" : "Comment"}
        </Button>
      </div>
    </div>
  );
};

export default CommentThread;